import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import Bare from '../components/Bare';
import { productionAPI } from '../api';
import toast from 'react-hot-toast';
import { ShiftSummary } from './Director';

// ─── Куда у пресса уходит время ──────────────────────────────────────────────
//
// Выпуск было видно и раньше: столько-то листов за смену. А «почему мало» —
// не было видно никак: сорок минут поломки и сорок минут ровной работы в
// цифрах выглядели одинаково.
//
// Здесь время разложено на работу и остановки, а остановки — по причинам.
// Первое, что спрашивает владелец, — что съедает больше всего, поэтому свод
// по причинам стоит выше разбора по прессам.

// Причины теперь из справочника — цвет держим за системными кодами, а всё,
// что администратор добавил сам, рисуем нейтрально.
const REASON_COLOR = {
  retool:      '#f59e0b',
  setup:       '#eab308',
  dsp:         '#0ea5e9',
  film:        '#8b5cf6',
  repair:      '#ef4444',
  break:       '#6366f1',
  no_material: '#a855f7',
  idle:        '#64748b',
  other:       '#94a3b8',
};
const rcolor = (x) => REASON_COLOR[x && x.code] || '#94a3b8';

const todayStr = () => new Date().toISOString().split('T')[0];

// «2 ч 15 мин» читается сразу, 8100 секунд — нет.
function dur(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h} ${t('ч')} ${m} ${t('мин')}`;
  if (h) return `${h} ${t('ч')}`;
  if (m) return `${m} ${t('мин')}`;
  return `${s} ${t('сек')}`;
}

function hhmm(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function dmy(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 14, padding: 18,
};

// Полоса: прессование / отмеченные остановки / необъяснённое время.
// Доли видно глазом, без деления в уме.
function Bar({ active, stops, idle }) {
  const total = (active || 0) + (stops || 0) + (idle || 0);
  if (!total) return null;
  const pc = (x) => `${((x || 0) / total) * 100}%`;
  return (
    <div style={{
      display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden',
      background: 'var(--bg)', marginTop: 10,
    }}>
      <div style={{ width: pc(active), background: '#22c55e' }} title="прессование" />
      <div style={{ width: pc(stops), background: '#f59e0b' }} title="остановки" />
      <div style={{ width: pc(idle), background: '#475569' }} title="не объяснено" />
    </div>
  );
}

function StopsBreakdown({ stops }) {
  if (!stops || !stops.length) {
    return <div style={{ color: 'var(--dim)', fontSize: 14 }}>{t('Остановок не отмечено')}</div>;
  }
  const max = Math.max(...stops.map(s => s.seconds)) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {stops.map(s => (
        <div key={s.reason}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
            <span style={{ color: 'var(--text)', fontWeight: 600 }}>
              {t(s.label)}
              <span style={{ color: 'var(--dim)', fontWeight: 400 }}> · {s.count} {t('раз')}</span>
            </span>
            <b style={{ color: rcolor(s) }}>{dur(s.seconds)}</b>
          </div>
          <div style={{ height: 8, background: 'var(--bg)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.round((s.seconds / max) * 100)}%`, height: '100%',
              background: rcolor(s),
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PressTimePage({ bare }) {
  const Shell = bare ? Bare : Layout;

  const [from, setFrom]       = useState(todayStr());
  const [to, setTo]           = useState(todayStr());
  const [pressId, setPressId] = useState('');
  const [presses, setPresses] = useState([]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    productionAPI.getPresses().then(r => setPresses(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { date_from: from, date_to: to };
    if (pressId) params.press_id = pressId;
    productionAPI.getPressTime(params)
      .then(r => setData(r.data))
      .catch(() => toast.error(t('Ошибка загрузки')))
      .finally(() => setLoading(false));
  }, [from, to, pressId]);

  useEffect(() => { load(); }, [load]);

  const rows   = data?.presses || [];
  const totals = data?.totals || {};
  const span   = (totals.work_seconds || 0) + (totals.stop_seconds || 0);
  // Легенда к полосе — три цвета значат три разных вида времени
  const legend = [
    { c: '#22c55e', l: 'прессование' },
    { c: '#f59e0b', l: 'остановки' },
    { c: '#475569', l: 'не объяснено' },
  ];
  const share  = span ? Math.round((totals.stop_seconds / span) * 100) : 0;

  const inp = {
    padding: '8px 12px', background: 'var(--input)', border: '1px solid var(--border)',
    borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none',
    fontFamily: 'inherit',
  };
  const lbl = {
    fontSize: 11, color: 'var(--dim)', fontWeight: 700,
    textTransform: 'uppercase', marginBottom: 6,
  };

  return (
    <Shell title={t('Время прессов')}>

      {/* Сколько сегодня сделали — над разбором того, куда ушло время.
          Порознь эти две вещи мало что значат: «простой три часа» звучит
          одинаково и в день на тысячу листов, и в день на двести. */}
      <ShiftSummary />

      <div style={{ ...CARD, marginBottom: 18, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={lbl}>{t('С')}</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} />
        </div>
        <div>
          <div style={lbl}>{t('По')}</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} />
        </div>
        <div>
          <div style={lbl}>{t('Пресс')}</div>
          <select value={pressId} onChange={e => setPressId(e.target.value)} style={inp}>
            <option value="">{t('Все')}</option>
            {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <button onClick={load} style={{ ...inp, cursor: 'pointer', fontWeight: 700 }}>🔄 {t('Обновить')}</button>
        {loading && <span style={{ color: 'var(--dim)', fontSize: 13 }}>{t('Загрузка')}…</span>}
      </div>

      {/* Свод: сколько работали, сколько стояли */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 14, marginBottom: 18,
      }}>
        <div style={CARD}>
          <div style={lbl}>{t('Время смен')}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)' }}>
            {dur(totals.work_seconds)}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 2 }}>
            {t('от старта до завершения')}
          </div>
        </div>
        <div style={CARD}>
          <div style={lbl}>{t('Прессование')}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#22c55e' }}>
            {dur(totals.active_seconds)}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 2 }}>
            {t('пресс выдавал листы')}
            {/* Часть листов приходит от моноблока пачкой, интервал у них
                измерить нельзя — они посчитаны по медианному циклу */}
            {totals.active_measured != null &&
             totals.active_measured < totals.active_seconds && (
              <> · {t('замер')} {dur(totals.active_measured)}</>
            )}
          </div>
        </div>
        <div style={CARD}>
          <div style={lbl}>{t('Остановки')}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#f59e0b' }}>
            {dur(totals.stop_seconds)}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 2 }}>
            {share}% {t('от общего времени')}
          </div>
        </div>
        {/* Главная цифра: время, о котором нельзя сказать ничего, кроме того,
            что оно прошло. Ради неё кнопка остановки и появилась. */}
        <div style={{ ...CARD, borderColor: '#475569' }}>
          <div style={lbl}>{t('Не объяснено')}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#94a3b8' }}>
            {dur(totals.unexplained_seconds)}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 13, marginTop: 2 }}>
            {t('смена шла, пресс не работал')}
          </div>
        </div>
        <div style={CARD}>
          <div style={lbl}>{t('Выпуск')}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)' }}>
            {totals.sheets || 0} <span style={{ fontSize: 15, color: 'var(--dim)' }}>{t('лист')}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        {legend.map(x => (
          <span key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 7,
                                   color: 'var(--muted)', fontSize: 13 }}>
            <span style={{ width: 14, height: 10, borderRadius: 3, background: x.c }} />
            {t(x.l)}
          </span>
        ))}
      </div>

      {/* Что съедает время — на все прессы сразу */}
      <div style={{ ...CARD, marginBottom: 18 }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 14 }}>
          ⏸ {t('Из-за чего стояли')}
        </div>
        <StopsBreakdown stops={totals.stops} />
      </div>

      {/* Разбор по прессам */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
        {rows.map(r => (
          <div key={r.press_id} style={CARD}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{r.press}</div>
              {r.is_stopped && (
                <span style={{
                  background: '#ef444422', color: '#ef4444', borderRadius: 20,
                  padding: '3px 12px', fontSize: 12, fontWeight: 800,
                }}>⏸ {t('стоит сейчас')}</span>
              )}
              <div style={{ marginLeft: 'auto', color: 'var(--muted)', fontSize: 14 }}>
                {r.sheets} {t('лист')} · {r.jobs} {t('смен')}
                {/* Смену забыли завершить — это видно здесь, а не в виде
                    приписанных ей лишних часов работы */}
                {r.open_jobs > 0 && (
                  <span style={{ color: '#f59e0b' }}> · {r.open_jobs} {t('не завершено')}</span>
                )}
              </div>
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 12, marginTop: 14,
            }}>
              <div>
                <div style={lbl}>{t('Время смен')}</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{dur(r.work_seconds)}</div>
              </div>
              <div>
                <div style={lbl}>{t('Прессование')}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>{dur(r.active_seconds)}</div>
              </div>
              <div>
                <div style={lbl}>{t('Остановки')}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{dur(r.stop_seconds)}</div>
              </div>
              <div>
                <div style={lbl}>{t('Не объяснено')}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>{dur(r.unexplained_seconds)}</div>
              </div>
              <div>
                <div style={lbl}>{t('Цикл, сек')}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                  {r.cycle_mid != null ? r.cycle_mid : '—'}
                  {/* Медиана честнее среднего: один длинный интервал в конце
                      смены поднимает среднее так, что цифра теряет смысл */}
                  {r.cycle_avg != null && (
                    <span style={{ fontSize: 13, color: 'var(--dim)', fontWeight: 400 }}>
                      {' '}({t('сред')} {r.cycle_avg})
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Bar active={r.active_seconds} stops={r.stop_seconds} idle={r.unexplained_seconds} />

            {r.stops && r.stops.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <StopsBreakdown stops={r.stops} />
              </div>
            )}
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div style={{ ...CARD, color: 'var(--dim)', textAlign: 'center' }}>
            {t('За этот период работы не было')}
          </div>
        )}
      </div>

      {/* Каждая остановка отдельной строкой: «замена матрицы 14:20 — 15:05» */}
      {data?.rows?.length > 0 && (
        <div style={CARD}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>
            📋 {t('Остановки по порядку')}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ color: 'var(--dim)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Дата')}</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Пресс')}</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Причина')}</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Начало')}</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Конец')}</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Длительность')}</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{t('Оператор')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(s => (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>{dmy(s.started_at)}</td>
                    <td style={{ padding: '8px 10px' }}>{s.press}</td>
                    <td style={{ padding: '8px 10px', color: rcolor(s), fontWeight: 700 }}>
                      {t(s.label)}
                      {s.note && <span style={{ color: 'var(--dim)', fontWeight: 400 }}> · {s.note}</span>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{hhmm(s.started_at)}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {s.is_open
                        ? <span style={{ color: '#ef4444', fontWeight: 700 }}>{t('идёт')}</span>
                        : hhmm(s.ended_at)}
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>{dur(s.seconds)}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>{s.operator || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Shell>
  );
}
